<template>
  <div class="deploys-page">
    <div class="deploys-header">
      <h4 class="deploys-title">Pipeline</h4>
      <p class="deploys-subtitle">{{ subtitleText }}</p>
    </div>

    <div class="deploys-tabs-row">
      <div class="deploys-env-tabs">
        <button
          class="deploys-env-tab"
          :class="{ active: activeStatus === 'aguardando_testes' }"
          @click="setActiveStatus('aguardando_testes')"
        >
          <i class="fas fa-flask"></i>
          Homologação
        </button>
        <button
          class="deploys-env-tab"
          :class="{ active: activeStatus === 'aguardando_deploy' }"
          @click="setActiveStatus('aguardando_deploy')"
        >
          <i class="fas fa-rocket"></i>
          Produção
        </button>
      </div>

      <div class="deploys-view-tabs">
        <button
          class="deploys-view-tab"
          :class="{ active: viewMode === 'releases' }"
          @click="setViewMode('releases')"
        >
          <i class="fas fa-layer-group"></i>
          Releases
        </button>
        <button
          class="deploys-view-tab"
          :class="{ active: viewMode === 'bugs' }"
          @click="setViewMode('bugs')"
        >
          <i class="fas fa-bug"></i>
          Bugs
        </button>
      </div>
    </div>

    <div class="deploys-card">
      <div class="deploys-toolbar">
        <div class="deploys-search">
          <i class="fas fa-search"></i>
          <input
            type="text"
            class="form-control"
            :placeholder="viewMode === 'releases' ? 'Buscar por nome da release' : 'Buscar por título do chamado'"
            v-model="search"
          />
        </div>
        <div class="deploys-toolbar-right">
          <div class="deploys-stat">
            <span class="deploys-stat-icon">
              <i class="fas" :class="viewMode === 'releases' ? 'fa-cloud-upload-alt' : 'fa-bug'"></i>
            </span>
            <div class="deploys-stat-info">
              <span class="deploys-stat-number">{{ totalCount }}</span>
              <span class="deploys-stat-label">{{ activeStatus === 'aguardando_testes' ? 'aguardando testes' : 'aguardando deploy' }}</span>
            </div>
          </div>
          <button class="btn-refresh" :disabled="loading" @click="refetch">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            Atualizar
          </button>
        </div>
      </div>

      <div class="deploys-filters">
        <div class="deploys-filter">
          <label>Sistema</label>
          <v-select
            v-model="filters.project_id"
            :options="projects"
            :reduce="(p) => p.id"
            label="name"
            placeholder="Todos"
            :clearable="true"
          />
        </div>
        <label v-if="viewMode === 'releases' && activeStatus === 'aguardando_deploy'" class="deploys-show-finalized">
          <span class="deploys-toggle">
            <input type="checkbox" v-model="showFinalized" />
            <span class="deploys-toggle-slider"></span>
          </span>
          Exibir finalizados
        </label>
        <button
          v-if="hasActiveFilters"
          class="btn-clear-filters"
          @click="clearFilters"
        >
          <i class="fas fa-times"></i>
          Limpar filtros
        </button>
      </div>

      <div v-if="loading" class="deploys-state">
        <i class="fas fa-circle-notch fa-spin"></i>
        Carregando {{ viewMode === 'releases' ? 'releases' : 'chamados' }}...
      </div>

      <div v-else-if="currentList.length === 0" class="deploys-state">
        <i class="fas fa-check-circle deploys-state-icon"></i>
        <p>{{ viewMode === 'releases' ? 'Nenhuma release aguardando deploy.' : 'Nenhum bug aguardando deploy.' }}</p>
      </div>

      <div v-else class="table-responsive">
        <table v-if="viewMode === 'releases'" class="deploys-table">
          <thead>
            <tr>
              <th>Release</th>
              <th>Sistema</th>
              <th>Chamados</th>
              <th>PO</th>
              <th>Fechada em</th>
              <th>Aguardando</th>
              <th class="text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="release in releases" :key="release.id">
              <td data-label="Release">
                <span class="deploys-release-tag">
                  v{{ release.version_string }} — {{ release.name }}
                </span>
                <template v-for="t in pipelineTargets(release)">
                  <span
                    v-if="lastRunByTarget(release, t.key)"
                    :key="t.key"
                    class="deploys-last-run"
                    :class="'run-' + lastRunByTarget(release, t.key).status"
                    :title="lastRunByTarget(release, t.key).tooltip"
                  >
                    <i class="fas" :class="lastRunByTarget(release, t.key).icon"></i>
                    {{ t.targetLabel }}: {{ lastRunByTarget(release, t.key).label }}
                    <a
                      class="deploys-console-link"
                      title="Ver console do Jenkins"
                      @click="openConsole(lastRunByTarget(release, t.key).run)"
                    >
                      <i class="fas fa-terminal"></i>
                    </a>
                    <a
                      class="deploys-console-link"
                      title="Ver histórico / reverter"
                      @click="openHistory(release, t)"
                    >
                      <i class="fas fa-history"></i>
                    </a>
                  </span>
                </template>
              </td>
              <td data-label="Sistema">{{ systemName(release) }}</td>
              <td data-label="Chamados">
                <div class="deploys-tickets">
                  <a
                    v-for="ticket in (release.tickets || [])"
                    :key="ticket.id"
                    class="deploys-ticket-chip"
                    :title="ticket.title"
                    @click="viewTicket(ticket)"
                  >
                    #{{ ticket.id }}
                  </a>
                  <span v-if="!(release.tickets || []).length" class="text-muted">-</span>
                </div>
              </td>
              <td data-label="PO">{{ poName(release) }}</td>
              <td data-label="Fechada em">{{ release.data_fechamento_release_f || "-" }}</td>
              <td data-label="Aguardando">
                <span
                  class="deploys-dot"
                  :class="waitingInfo(release).className"
                ></span>
                {{ waitingInfo(release).label }}
              </td>
              <td data-label="Ações" class="text-center">
                <div class="deploys-actions">
                  <template v-if="canRun(release)">
                    <button
                      v-for="t in pipelineTargets(release)"
                      :key="t.key"
                      class="btn-run-pipeline"
                      :disabled="!canRunTarget(release, t.key)"
                      @click="runPipeline(release, t.key)"
                    >
                      <i
                        class="fas"
                        :class="!canRunTarget(release, t.key) ? 'fa-circle-notch fa-spin' : 'fa-rocket'"
                      ></i>
                      {{ runButtonLabel(release, t) }}
                    </button>
                  </template>
                  <button
                    v-if="pipelineTargets(release).length === 0 && release.project"
                    class="deploys-no-job-btn"
                    title="Cadastrar job do Jenkins pra esse sistema"
                    @click="openJenkinsJobModal(release.project)"
                  >
                    <i class="fas fa-exclamation-triangle"></i>
                    Sem job configurado
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table v-else class="deploys-table">
          <thead>
            <tr>
              <th>Chamado</th>
              <th>Sistema</th>
              <th>Aberto por</th>
              <th class="text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="call in calls" :key="call.id">
              <td data-label="Chamado">
                <a class="deploys-release-tag" @click="viewTicket(call)">
                  #{{ call.id }} — {{ call.title }}
                </a>
                <template v-for="t in pipelineTargets(call)">
                  <span
                    v-if="lastRunByTarget(call, t.key)"
                    :key="t.key"
                    class="deploys-last-run"
                    :class="'run-' + lastRunByTarget(call, t.key).status"
                    :title="lastRunByTarget(call, t.key).tooltip"
                  >
                    <i class="fas" :class="lastRunByTarget(call, t.key).icon"></i>
                    {{ t.targetLabel }}: {{ lastRunByTarget(call, t.key).label }}
                    <a
                      class="deploys-console-link"
                      title="Ver console do Jenkins"
                      @click="openConsole(lastRunByTarget(call, t.key).run)"
                    >
                      <i class="fas fa-terminal"></i>
                    </a>
                    <a
                      class="deploys-console-link"
                      title="Ver histórico / reverter"
                      @click="openHistory(call, t)"
                    >
                      <i class="fas fa-history"></i>
                    </a>
                  </span>
                </template>
              </td>
              <td data-label="Sistema">{{ call.project ? call.project.name : "-" }}</td>
              <td data-label="Aberto por">{{ call.user_model ? call.user_model.name : "-" }}</td>
              <td data-label="Ações" class="text-center">
                <div class="deploys-actions">
                  <template v-if="canRun(call)">
                    <button
                      v-for="t in pipelineTargets(call)"
                      :key="t.key"
                      class="btn-run-pipeline"
                      :disabled="!canRunTarget(call, t.key)"
                      @click="runPipeline(call, t.key)"
                    >
                      <i
                        class="fas"
                        :class="!canRunTarget(call, t.key) ? 'fa-circle-notch fa-spin' : 'fa-rocket'"
                      ></i>
                      {{ runButtonLabel(call, t) }}
                    </button>
                  </template>
                  <button
                    v-if="pipelineTargets(call).length === 0 && call.project"
                    class="deploys-no-job-btn"
                    title="Cadastrar job do Jenkins pra esse sistema"
                    @click="openJenkinsJobModal(call.project)"
                  >
                    <i class="fas fa-exclamation-triangle"></i>
                    Sem job configurado
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="totalCount > perPage" class="deploys-paginator">
        <small class="text-muted">
          Página {{ page }} de {{ lastPage }} — {{ totalCount }} {{ viewMode === 'releases' ? 'release(s)' : 'chamado(s)' }}
        </small>
        <div class="deploys-paginator-actions">
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="page <= 1 || loading"
            @click="goToPage(page - 1)"
          >
            ‹ Anterior
          </button>
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="page >= lastPage || loading"
            @click="goToPage(page + 1)"
          >
            Próxima ›
          </button>
        </div>
      </div>
    </div>

    <jenkins-job-modal ref="jenkinsJobModal" @saved="refetch" />
    <deploy-console-modal ref="deployConsoleModal" />
    <deploy-history-modal ref="deployHistoryModal" />
  </div>
</template>

<script>
import axios from "axios";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import JenkinsJobModal from "../../components/JenkinsJobModal";
import DeployConsoleModal from "../../components/DeployConsoleModal";
import DeployHistoryModal from "../../components/DeployHistoryModal";

export default {
  components: { vSelect, JenkinsJobModal, DeployConsoleModal, DeployHistoryModal },
  data() {
    return {
      activeStatus: "aguardando_deploy",
      viewMode: "releases",
      showFinalized: false,
      releases: [],
      calls: [],
      loading: false,
      search: "",
      searchTimeout: null,
      page: 1,
      perPage: 15,
      totalCount: 0,
      lastPage: 1,
      runningPipelineKey: null,
      filters: {
        project_id: null,
      },
      projects: [],
    };
  },
  computed: {
    hasActiveFilters() {
      return !!this.filters.project_id;
    },
    currentList() {
      return this.viewMode === "releases" ? this.releases : this.calls;
    },
    subtitleText() {
      const envLabel = this.activeStatus === "aguardando_testes" ? "homologação" : "produção";
      const itemLabel = this.viewMode === "releases" ? "Releases" : "Chamados de bug";
      const waitLabel = this.activeStatus === "aguardando_testes" ? "testes" : "deploy";
      return `${itemLabel} aguardando ${waitLabel} — dispare o pipeline de ${envLabel} direto por aqui.`;
    },
  },
  mounted() {
    this.refetch();
    this.getProjects();
  },
  watch: {
    search() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.refetch();
      }, 500);
    },
    filters: {
      deep: true,
      handler() {
        this.page = 1;
        this.refetch();
      },
    },
    showFinalized() {
      this.page = 1;
      this.refetch();
    },
  },
  methods: {
    async getProjects() {
      try {
        const api = this.$store.state.api + "projects?paginated=false";
        const res = await axios.get(api);
        this.projects = res.data.data || [];
      } catch (e) {
        this.projects = [];
      }
    },
    clearFilters() {
      this.filters.project_id = null;
    },
    setActiveStatus(status) {
      if (this.activeStatus === status) return;
      this.activeStatus = status;
      this.page = 1;
      this.refetch();
    },
    setViewMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      this.page = 1;
      this.search = "";
      this.filters.project_id = null;
      this.refetch();
    },
    refetch() {
      if (this.viewMode === "releases") {
        this.fetchDeploys();
      } else {
        this.fetchBugs();
      }
    },
    async fetchDeploys() {
      this.loading = true;
      try {
        const api = this.$store.state.api + "releases";
        const showBothStatuses = this.activeStatus === "aguardando_deploy" && this.showFinalized;
        const params = {
          status: showBothStatuses ? ["aguardando_deploy", "finalizada"] : this.activeStatus,
          limit: this.perPage,
          page: this.page,
          with: ["project", "tickets", "statusHistories", "deployRuns.user"],
        };
        if (this.search) {
          params.name = this.search;
        }
        if (this.filters.project_id) {
          params.project_id = this.filters.project_id;
        }

        const res = await axios.get(api, { params });

        this.releases = res.data.data || [];
        this.totalCount = res.data.total || 0;
        this.lastPage = res.data.last_page || 1;
      } catch (e) {
        this.$message(
          "Ops",
          "Não foi possível carregar as releases aguardando deploy.",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchBugs() {
      this.loading = true;
      try {
        const api = this.$store.state.api + "bug-calls";
        const params = {
          status: this.activeStatus,
          limit: this.perPage,
          page: this.page,
        };
        if (this.search) {
          params.name = this.search;
        }
        if (this.filters.project_id) {
          params.project_id = this.filters.project_id;
        }

        const res = await axios.get(api, { params });

        this.calls = res.data.data || [];
        this.totalCount = res.data.total || 0;
        this.lastPage = res.data.last_page || 1;
      } catch (e) {
        this.$message(
          "Ops",
          "Não foi possível carregar os bugs aguardando deploy.",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.lastPage || page === this.page) return;
      this.page = page;
      this.refetch();
    },
    poName(release) {
      return release.po ? release.po.name : "-";
    },
    systemName(release) {
      return release.project ? release.project.name : "-";
    },
    waitingInfo(release) {
      const histories = release.status_histories || [];
      const enteredDeploy = histories.find(
        (h) => h.status_novo === this.activeStatus
      );
      const reference =
        (enteredDeploy && enteredDeploy.created_at) ||
        release.updated_at ||
        release.created_at;
      if (!reference) return { label: "-", className: "" };

      const days = Math.floor(
        (Date.now() - new Date(reference).getTime()) / 86400000
      );
      const label = days <= 0 ? "hoje" : `há ${days}d`;

      if (days <= 2) return { label, className: "dot-green" };
      if (days <= 5) return { label, className: "dot-yellow" };
      return { label, className: "dot-red" };
    },
    relativeTime(dateStr) {
      const diffMs = Date.now() - new Date(dateStr).getTime();
      const minutes = Math.floor(diffMs / 60000);
      if (minutes < 1) return "agora";
      if (minutes < 60) return `há ${minutes}min`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `há ${hours}h`;
      return `há ${Math.floor(hours / 24)}d`;
    },
    lastRunByTarget(item, target) {
      const runs = (item.deploy_runs || []).filter((r) => r.target === target);
      if (runs.length === 0) return null;
      const run = runs[0];

      const STATUS_MAP = {
        triggered: { icon: "fa-circle-notch fa-spin", label: "Rodando..." },
        running: { icon: "fa-circle-notch fa-spin", label: "Rodando..." },
        success: { icon: "fa-check-circle", label: "Sucesso" },
        failed: { icon: "fa-times-circle", label: "Falhou" },
      };
      const info = STATUS_MAP[run.status] || STATUS_MAP.failed;
      const who = run.user ? run.user.name : "alguém";

      return {
        run,
        status: run.status,
        icon: info.icon,
        label: `${info.label} ${this.relativeTime(run.created_at)}`,
        tooltip: `Branch: ${run.branch || "-"} · Disparado por ${who}`,
      };
    },
    viewTicket(ticket) {
      const url = `${window.location.origin}/calls/ticket/${ticket.id}`;
      window.open(url, "_blank");
    },
    openJenkinsJobModal(project) {
      this.$refs.jenkinsJobModal.open(project);
    },
    openConsole(run) {
      this.$refs.deployConsoleModal.open(run);
    },
    openHistory(item, target) {
      const filterParams = this.viewMode === "releases"
        ? { release_id: item.id }
        : { call_id: item.id };
      this.$refs.deployHistoryModal.open(filterParams, target.key, target.targetLabel);
    },
    isHml(item) {
      if (this.viewMode === "releases") {
        return item.status === "aguardando_testes";
      }
      return Number(item.status) === 22;
    },
    canRun(item) {
      if (this.viewMode === "releases") {
        return item.status === "aguardando_testes" || item.status === "aguardando_deploy";
      }
      return Number(item.status) === 22 || Number(item.status) === 18;
    },
    pipelineTargets(item) {
      const project = item.project;
      if (!project) return [];
      const isHml = this.isHml(item);
      const frontJob = isHml ? project.jenkins_job_front_hml : project.jenkins_job_front;
      const apiJob = isHml ? project.jenkins_job_api_hml : project.jenkins_job_api;
      const suffix = isHml ? " (HML)" : "";

      const targets = [];
      if (frontJob) targets.push({ key: "front", targetLabel: "Front" });
      if (apiJob) targets.push({ key: "api", targetLabel: "API" });
      if (targets.length === 1) {
        return [{ key: targets[0].key, targetLabel: targets[0].targetLabel, label: "Rodar Pipeline" + suffix }];
      }
      return targets.map((t) => ({ key: t.key, targetLabel: t.targetLabel, label: `Rodar ${t.targetLabel}${suffix}` }));
    },
    isTargetBusy(item, targetKey) {
      const last = this.lastRunByTarget(item, targetKey);
      return !!last && (last.status === "triggered" || last.status === "running");
    },
    canRunTarget(item, targetKey) {
      const key = `${item.id}:${targetKey}`;
      return this.runningPipelineKey !== key && !this.isTargetBusy(item, targetKey);
    },
    runButtonLabel(item, t) {
      const key = `${item.id}:${t.key}`;
      if (this.runningPipelineKey === key) return "Disparando...";
      if (this.isTargetBusy(item, t.key)) return "Rodando...";
      return t.label;
    },
    async runPipeline(item, target) {
      const key = `${item.id}:${target}`;
      this.runningPipelineKey = key;
      try {
        const endpoint = this.viewMode === "releases" ? `deploys/${item.id}/run` : `bug-calls/${item.id}/run`;
        const api = this.$store.state.api + endpoint;
        const res = await axios.post(api, { target });
        this.$message(
          "Sucesso",
          res.data.message || "Pipeline disparada com sucesso.",
          "success"
        );
        this.refetch();
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          "Não foi possível disparar o pipeline.";
        this.$message("Ops", msg, "error");
      } finally {
        this.runningPipelineKey = null;
      }
    },
  },
};
</script>

<style scoped>
.deploys-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.deploys-title {
  font-weight: 700;
  color: #15488a;
  margin-bottom: 2px;
}

.deploys-subtitle {
  color: #6e6b7b;
  margin-bottom: 0;
  font-size: 0.9rem;
}

.deploys-stat {
  background: #fff9e6;
  border: 1px solid #f1c21b;
  border-radius: 8px;
  padding: 7px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.deploys-stat-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #f1c21b;
  color: #4a3c00;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.deploys-stat-info {
  display: flex;
  flex-direction: column;
}

.deploys-stat-number {
  font-size: 1.35rem;
  font-weight: 700;
  color: #4a3c00;
  line-height: 1;
}

.deploys-stat-label {
  font-size: 0.72rem;
  color: #8a6d00;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
}

.deploys-card {
  background: #fff;
  border: 1px solid #d6d6d6;
  border-radius: 8px;
  padding: 20px;
}

.deploys-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.deploys-toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.deploys-search {
  position: relative;
  flex: 1;
  min-width: 240px;
  max-width: 380px;
}

.deploys-search i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #b9b9c3;
  font-size: 0.85rem;
}

.deploys-search .form-control {
  padding-left: 34px;
  border-radius: 6px;
}

.deploys-filters {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebe9f1;
}

.deploys-filter {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 220px;
}

.deploys-filter label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6e6b7b;
  margin-bottom: 0;
}

.deploys-show-finalized {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #6e6b7b;
  margin-bottom: 0;
  padding: 8px 4px;
  cursor: pointer;
}

.deploys-toggle {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 20px;
  flex-shrink: 0;
}

.deploys-toggle input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}

.deploys-toggle-slider {
  position: absolute;
  inset: 0;
  background: #d8d6de;
  border-radius: 999px;
  transition: background 0.15s;
}

.deploys-toggle-slider::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: transform 0.15s;
}

.deploys-toggle input:checked + .deploys-toggle-slider {
  background: #15488a;
}

.deploys-toggle input:checked + .deploys-toggle-slider::before {
  transform: translateX(14px);
}

.btn-clear-filters {
  border: none;
  background: none;
  color: #bb0034;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 8px 4px;
}

.btn-clear-filters:hover {
  text-decoration: underline;
}

.btn-refresh {
  border: 2px solid #15488a;
  background: #fff;
  color: #15488a;
  border-radius: 0.25rem;
  font-weight: 600;
  padding: 6px 16px;
  font-size: 0.85rem;
}

.btn-refresh:hover:not(:disabled) {
  background: #15488a;
  color: #fff;
}

.btn-refresh:disabled {
  opacity: 0.6;
}

.deploys-tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.deploys-env-tabs,
.deploys-view-tabs {
  display: flex;
  gap: 8px;
}

.deploys-env-tab {
  border: 2px solid #15488a;
  background: #fff;
  color: #15488a;
  border-radius: 0.25rem;
  font-weight: 600;
  padding: 7px 18px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.deploys-env-tab:hover {
  background: #eef3fa;
}

.deploys-env-tab.active {
  background: #15488a;
  color: #fff;
}

.deploys-view-tab {
  border: 1px solid #d6d6d6;
  background: #f8f8fb;
  color: #5e5873;
  border-radius: 0.25rem;
  font-weight: 600;
  padding: 7px 16px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.deploys-view-tab:hover {
  background: #ebe9f1;
}

.deploys-view-tab.active {
  background: #5e5873;
  color: #fff;
  border-color: #5e5873;
}

.deploys-state {
  text-align: center;
  padding: 48px 0;
  color: #6e6b7b;
}

.deploys-state-icon {
  font-size: 1.8rem;
  color: #24a148;
  display: block;
  margin-bottom: 10px;
}

.deploys-table {
  width: 100%;
  border-collapse: collapse;
}

.deploys-table thead th {
  text-align: left;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6e6b7b;
  border-bottom: 2px solid #ebe9f1;
  padding: 10px 12px;
}

.deploys-table tbody td {
  padding: 14px 12px;
  border-bottom: 1px solid #ebe9f1;
  vertical-align: middle;
  font-size: 0.9rem;
}

.deploys-table tbody tr:hover {
  background: #f8f8fb;
}

.deploys-last-run {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  margin-top: 5px;
}

.deploys-last-run.run-success {
  color: #24a148;
}

.deploys-last-run.run-failed {
  color: #da1e28;
}

.deploys-last-run.run-triggered,
.deploys-last-run.run-running {
  color: #0f61f9;
}

.deploys-console-link {
  margin-left: 6px;
  color: #6e6b7b;
  cursor: pointer;
}

.deploys-console-link:hover {
  color: #15488a;
}

body.dark-layout .deploys-console-link {
  color: #b4b7bd;
}

body.dark-layout .deploys-console-link:hover {
  color: #5a9bf0;
}

.deploys-release-tag {
  display: inline-block;
  background: #eaf0fa;
  color: #15488a;
  border: 1px solid #c9d9ef;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.deploys-tickets {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  max-width: 220px;
}

.deploys-ticket-chip {
  display: inline-block;
  background: #f4f4f7;
  color: #5e5873;
  border: 1px solid #ebe9f1;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.deploys-ticket-chip:hover {
  background: #ebe9f1;
}

.deploys-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 6px;
}

.deploys-dot.dot-green {
  background: #24a148;
}

.deploys-dot.dot-yellow {
  background: #f1c21b;
}

.deploys-dot.dot-red {
  background: #da1e28;
}

.deploys-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.deploys-no-job-btn {
  border: 1px dashed #d29922;
  background: #fff8e6;
  color: #9a6700;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 7px 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.deploys-no-job-btn:hover {
  background: #fff1cc;
}

body.dark-layout .deploys-no-job-btn {
  background: rgba(210, 153, 34, 0.12);
  color: #d29922;
  border-color: rgba(210, 153, 34, 0.4);
}

body.dark-layout .deploys-no-job-btn:hover {
  background: rgba(210, 153, 34, 0.2);
}

.btn-run-pipeline {
  border: none;
  background: #f1c21b;
  color: #4a3c00;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 8px 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.btn-run-pipeline:hover:not(:disabled) {
  background: #d9ac0b;
}

.btn-run-pipeline:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.deploys-paginator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.deploys-paginator-actions {
  display: flex;
  gap: 8px;
}

/* Modo dark */
body.dark-layout .deploys-title {
  color: #5a9bf0;
}

body.dark-layout .deploys-subtitle {
  color: #b4b7bd;
}

body.dark-layout .deploys-card {
  background: #283046;
  border-color: #404656;
}

body.dark-layout .deploys-stat {
  background: rgba(241, 194, 27, 0.12);
}

body.dark-layout .deploys-stat-number {
  color: #f1c21b;
}

body.dark-layout .deploys-stat-label {
  color: #d9ac0b;
}

body.dark-layout .btn-refresh {
  background: transparent;
  border-color: #5a9bf0;
  color: #5a9bf0;
}

body.dark-layout .btn-refresh:hover:not(:disabled) {
  background: #5a9bf0;
  color: #161d31;
}

body.dark-layout .deploys-state {
  color: #b4b7bd;
}

body.dark-layout .deploys-env-tab {
  background: transparent;
  border-color: #5a9bf0;
  color: #5a9bf0;
}

body.dark-layout .deploys-env-tab:hover {
  background: rgba(90, 155, 240, 0.12);
}

body.dark-layout .deploys-env-tab.active {
  background: #5a9bf0;
  color: #161d31;
}

body.dark-layout .deploys-view-tab {
  background: rgba(180, 183, 189, 0.08);
  border-color: #404656;
  color: #b4b7bd;
}

body.dark-layout .deploys-view-tab:hover {
  background: rgba(180, 183, 189, 0.16);
}

body.dark-layout .deploys-view-tab.active {
  background: #5a9bf0;
  color: #161d31;
  border-color: #5a9bf0;
}

body.dark-layout .deploys-table thead th {
  color: #b4b7bd;
  border-bottom-color: #404656;
}

body.dark-layout .deploys-table tbody td {
  border-bottom-color: #404656;
}

body.dark-layout .deploys-table tbody tr:hover {
  background: #232a3d;
}

body.dark-layout .deploys-filters {
  border-bottom-color: #404656;
}

body.dark-layout .deploys-filter label {
  color: #b4b7bd;
}

body.dark-layout .deploys-show-finalized {
  color: #b4b7bd;
}

body.dark-layout .deploys-toggle-slider {
  background: #3b4253;
}

body.dark-layout .deploys-toggle input:checked + .deploys-toggle-slider {
  background: #5a9bf0;
}

body.dark-layout .deploys-release-tag {
  background: rgba(90, 155, 240, 0.14);
  color: #5a9bf0;
  border-color: rgba(90, 155, 240, 0.35);
}

body.dark-layout .deploys-ticket-chip {
  background: rgba(180, 183, 189, 0.1);
  color: #b4b7bd;
  border-color: #404656;
}

body.dark-layout .deploys-ticket-chip:hover {
  background: rgba(180, 183, 189, 0.18);
}

/* Responsivo — abaixo de 768px a tabela vira uma lista de cards */
@media (max-width: 768px) {
  .deploys-card {
    padding: 14px;
  }

  .deploys-toolbar-right {
    width: 100%;
    justify-content: space-between;
  }

  .deploys-search {
    max-width: none;
  }

  .deploys-filter {
    min-width: 0;
    flex: 1;
  }

  .deploys-table thead {
    display: none;
  }

  .deploys-table,
  .deploys-table tbody,
  .deploys-table tr,
  .deploys-table td {
    display: block;
    width: 100%;
  }

  .deploys-table tbody tr {
    margin-bottom: 14px;
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    padding: 10px 12px;
  }

  .deploys-table tbody tr:hover {
    background: none;
  }

  .deploys-table td {
    border-bottom: none;
    padding: 7px 0;
  }

  .deploys-table td::before {
    content: attr(data-label);
    display: block;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6e6b7b;
    margin-bottom: 3px;
  }

  .deploys-table td[data-label="Ações"] {
    text-align: left;
  }

  .deploys-actions {
    justify-content: flex-start;
  }

  .deploys-tickets {
    max-width: none;
  }

  .deploys-paginator {
    flex-direction: column;
    align-items: flex-start;
  }

  body.dark-layout .deploys-table tbody tr {
    border-color: #404656;
  }

  body.dark-layout .deploys-table td::before {
    color: #b4b7bd;
  }
}
</style>
